import React, { useState } from 'react';
import { Modal, Icon } from './ui';
import { changeMyPassword } from '../lib/usersApi';
import './ApprovalWorkflow.css';
import { useTranslation } from 'react-i18next'; // reuse .apr-notice styling

const errorStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  borderRadius: 8,
  background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
  border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
  color: 'var(--danger)',
  fontSize: 13,
};

interface ChangePasswordModalProps {
  apiUrl: string;
  /** When true, the user is forced to change (first login / after reset) — no current password needed. */
  forced?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/** Client-side mirror of the backend password policy for instant feedback. */
function validate(pw: string): string | null {
  if (!pw || pw.length < 8) return 'Password must be at least 8 characters';
  if (!/[a-z]/.test(pw) || !/[A-Z]/.test(pw)) return 'Password must include upper and lower case letters';
  if (!/[0-9]/.test(pw)) return 'Password must include a number';
  return null;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  apiUrl,
  forced = false,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setError(null);
    const policyErr = validate(next);
    if (policyErr) return setError(policyErr);
    if (next !== confirm) return setError('Passwords do not match');
    if (!forced && !current) return setError('Enter your current password');

    setSaving(true);
    try {
      await changeMyPassword(apiUrl, next, forced ? undefined : current);
      onSuccess();
    } catch (e: any) {
      setError(e.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={forced ? t('components.changePasswordModal.setANewPassword') : t('components.changePasswordModal.changePassword')}
      icon="key"
      onClose={forced ? () => {} : onClose}
      maxWidth={440}
      footer={
        <>
          {!forced && (
            <button className="ent-btn ghost" onClick={onClose} disabled={saving}>{t('components.changePasswordModal.cancel')}</button>
          )}
          <button className="ent-btn primary" onClick={submit} disabled={saving}>
            <Icon name="key" /> {saving ? t('components.changePasswordModal.saving') : t('components.changePasswordModal.updatePassword')}
          </button>
        </>
      }
    >
      {forced && (
        <div className="apr-notice apr-notice-warn" style={{ marginBottom: 16 }}>
          <Icon name="lock" />
          <div>{t('components.changePasswordModal.yourPasswordMustBeChangedBefore')}</div>
        </div>
      )}
      {!forced && (
        <div className="ent-field">
          <label>{t('components.changePasswordModal.currentPassword')}</label>
          <input
            className="ent-input"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
      )}
      <div className="ent-field">
        <label>{t('components.changePasswordModal.newPassword')}</label>
        <input
          className="ent-input"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder={t('components.changePasswordModal.min8CharsUpperLowerNumber')}
        />
      </div>
      <div className="ent-field">
        <label>{t('components.changePasswordModal.confirmNewPassword')}</label>
        <input
          className="ent-input"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>
      {error && (
        <div style={errorStyle}>
          <Icon name="warning" size={15} /> {error}
        </div>
      )}
    </Modal>
  );
};
